import { io, Socket } from "socket.io-client";

/**
 * WebSocket client for real-time updates from the Control Plane
 * Uses singleton pattern to maintain a single connection across the app
 */
class WebSocketClient {
  private socket: Socket | null = null;
  private wsUrl: string | null;

  constructor() {
    // WebSocket is optional - if not configured, real-time features are disabled
    this.wsUrl = import.meta.env.VITE_WS_URL || null;

    if (!this.wsUrl) {
      console.log("[WebSocket] Disabled - VITE_WS_URL not configured (real-time updates unavailable)");
    }
  }

  /**
   * Establish WebSocket connection
   * Returns existing socket if already connected, or null if WebSocket is disabled
   */
  connect(): Socket | null {
    if (!this.wsUrl) {
      return null;  // WebSocket disabled
    }

    if (this.socket?.connected) {
      return this.socket;
    }

    const token = localStorage.getItem("auth_token");

    this.socket = io(this.wsUrl, {
      auth: { token },
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      timeout: 20000,
    });

    this.socket.on("connect", () => {
      console.log("[WebSocket] Connected to", this.wsUrl);
    });

    this.socket.on("disconnect", (reason) => {
      console.log("[WebSocket] Disconnected:", reason);
    });

    this.socket.on("connect_error", (error) => {
      console.error("[WebSocket] Connection error:", error.message);
    });

    this.socket.on("reconnect", (attemptNumber) => {
      console.log("[WebSocket] Reconnected after", attemptNumber, "attempts");
    });

    this.socket.on("reconnect_attempt", (attemptNumber) => {
      console.log("[WebSocket] Reconnection attempt", attemptNumber);
    });

    this.socket.on("reconnect_failed", () => {
      console.error("[WebSocket] Reconnection failed after all attempts");
    });

    return this.socket;
  }

  /**
   * Disconnect WebSocket connection
   */
  disconnect() {
    if (this.socket) {
      console.log("[WebSocket] Disconnecting...");
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get the socket instance
   */
  getSocket(): Socket | null {
    return this.socket;
  }
}

// Export singleton instance
export const websocketClient = new WebSocketClient();
